<template>
  <ehc-page v-if="userHasPermission(['superAdmin'])">
    <ehc-header
      :text="`User: ${userData.displayName || userData.email}`"
      :actions="headerActions"
      @back="goBack"
      @edit="editMode = true"
    ></ehc-header>

    <ehc-sheet v-if="!loading">
      <v-alert v-if="userDeactivated" type="warning" outlined class="mb-4">
        This user was deactivated on
        {{ formatDeactivatedAt(userData.deletedAt) }} and is hidden from the
        users list.
      </v-alert>

      <!-- User Profile Card -->
      <v-card class="mb-4" outlined>
        <v-card-title>User Profile</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col cols="12" md="3" class="d-flex justify-center align-start">
              <div class="text-center">
                <ehc-user-avatar
                  :user="userData"
                  :size="120"
                  class="mb-2"
                ></ehc-user-avatar>
                <div class="text-subtitle2 font-weight-bold">
                  {{ userData.displayName || "No Name" }}
                </div>
                <div class="text-caption text-grey">{{ userData.email }}</div>
              </div>
            </v-col>
            <v-col cols="12" md="9">
              <div class="d-flex flex-column gap-3">
                <!-- Basic Info -->
                <div>
                  <div class="text-overline font-weight-bold">
                    Basic Information
                  </div>
                  <v-simple-table dense>
                    <tbody>
                      <tr>
                        <td class="font-weight-bold" style="width: 150px">
                          User ID
                        </td>
                        <td>{{ userData.uid }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">Email</td>
                        <td>{{ userData.email }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">Display Name</td>
                        <td>{{ userData.displayName || "—" }}</td>
                      </tr>
                      <tr v-if="userData.firstName || userData.lastName">
                        <td class="font-weight-bold">Full Name</td>
                        <td>
                          {{ userData.firstName || "" }}
                          {{ userData.lastName || "" }}
                        </td>
                      </tr>
                      <tr v-if="userData.phoneNumber">
                        <td class="font-weight-bold">Phone</td>
                        <td>{{ userData.phoneNumber }}</td>
                      </tr>
                      <tr>
                        <td class="font-weight-bold">Account Created</td>
                        <td>{{ formatDate(userData.createdAt) }}</td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Permissions Card -->
      <v-card class="mb-4" outlined>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>Permissions & Roles</span>
          <v-btn
            v-if="!editMode"
            small
            text
            color="primary"
            @click="editMode = true"
          >
            <v-icon small left>mdi-pencil</v-icon>
            Edit
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <div v-if="editMode" class="mb-4">
            <div class="text-subtitle2 font-weight-bold mb-2">
              Select Permissions:
            </div>
            <v-switch
              v-for="(option, index) in permissionOptions"
              :key="index"
              v-model="editingUser.permissions"
              :label="option"
              :value="option"
              dense
              class="mb-1"
            ></v-switch>
            <v-row class="mt-4">
              <v-spacer></v-spacer>
              <v-btn small text color="grey" @click="editMode = false">
                Cancel
              </v-btn>
              <v-btn
                small
                color="primary"
                @click="savePermissions"
                :loading="savingPermissions"
              >
                Save Permissions
              </v-btn>
            </v-row>
          </div>
          <div v-else>
            <v-chip
              v-if="userData.permissions && userData.permissions.length > 0"
              v-for="(perm, index) in userData.permissions"
              :key="index"
              color="primary"
              text-color="white"
              class="mr-2 mb-2"
            >
              {{ perm }}
            </v-chip>
            <div v-else class="text-caption text-grey">
              No permissions assigned
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Organizations Card -->
      <v-card class="mb-4" outlined>
        <v-card-title>Associated Organizations</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <div v-if="userOrganizations.length > 0">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Organization Name</th>
                  <th>Organization ID</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="org in userOrganizations" :key="org.id">
                  <td>{{ org.displayName || org.name || "—" }}</td>
                  <td class="text-caption text-monospace">{{ org.id }}</td>
                  <td>
                    <v-btn
                      x-small
                      text
                      color="primary"
                      @click="goToOrganization(org.id)"
                    >
                      <v-icon x-small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-else class="text-caption text-grey">
            User is not associated with any organizations
          </div>
        </v-card-text>
      </v-card>

      <!-- Properties Card -->
      <v-card class="mb-4" outlined>
        <v-card-title>Associated Properties</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <div v-if="userProperties.length > 0">
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Property Address</th>
                  <th>Property ID</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="prop in userProperties" :key="prop.id">
                  <td>{{ prop.address || prop.nickname || "—" }}</td>
                  <td class="text-caption text-monospace">{{ prop.id }}</td>
                  <td>
                    <v-btn
                      x-small
                      text
                      color="primary"
                      @click="goToProperty(prop.id)"
                    >
                      <v-icon x-small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-else class="text-caption text-grey">
            User is not associated with any properties
          </div>
        </v-card-text>
      </v-card>

      <!-- Actions Card -->
      <v-card class="mb-4" outlined>
        <v-card-title>Actions</v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-row>
            <v-col cols="12" sm="6" md="3">
              <v-btn
                block
                outlined
                color="primary"
                @click="resetPassword"
                :loading="resettingPassword"
              >
                <v-icon left small>mdi-lock-reset</v-icon>
                Reset Password
              </v-btn>
            </v-col>
            <v-col cols="12" sm="6" md="3">
              <v-btn block outlined color="warning" @click="sendEmail">
                <v-icon left small>mdi-email</v-icon>
                Send Email
              </v-btn>
            </v-col>
            <v-col v-if="!userDeactivated" cols="12" sm="6" md="3">
              <v-btn block outlined color="error" @click="confirmDelete">
                <v-icon left small>mdi-delete</v-icon>
                Deactivate User
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </ehc-sheet>

    <!-- Dialogs -->
    <ehc-message
      v-model="dialog"
      @cancel="dialog.show = false"
      @confirm="sendPasswordReset()"
      @sendConfirm="sendEmailAction()"
      @deleteConfirm="deleteUser()"
    >
      <template #resetPassword>
        <div>
          <p>
            Are you sure you want to send a password reset email to
            <strong>{{ userData.email }}</strong
            >?
          </p>
          <p class="text-caption text-grey">
            The user will receive an email with a password reset link.
          </p>
        </div>
      </template>

      <template #sendEmail>
        <div>
          <p class="text-subtitle2 font-weight-bold mb-2">
            Send Email to {{ userData.email }}
          </p>
          <v-textarea
            v-model="emailMessage"
            label="Message"
            outlined
            dense
            rows="6"
            placeholder="Enter the email message here..."
          ></v-textarea>
        </div>
      </template>

      <template #deleteUser>
        <div>
          <v-alert type="error" outlined class="mb-3">
            <strong>Warning:</strong> This will deactivate the user account,
            disable login, and remove them from all organizations.
          </v-alert>
          <p>
            Are you sure you want to deactivate user
            <strong>{{ userData.email }}</strong
            >?
          </p>
          <p class="text-caption">Type the email address to confirm:</p>
          <v-text-field
            v-model="confirmDeleteEmail"
            outlined
            dense
            placeholder="Confirm by typing email"
          ></v-text-field>
        </div>
      </template>
    </ehc-message>

    <!-- Loading State -->
    <v-card
      v-if="loading"
      class="d-flex align-center justify-center"
      style="height: 300px"
    >
      <div class="text-center">
        <v-progress-circular
          indeterminate
          color="primary"
          class="mb-2"
        ></v-progress-circular>
        <p class="text-subtitle2">Loading user...</p>
      </div>
    </v-card>
  </ehc-page>
</template>

<script>
import firebase from "firebase";
import mixins from "@/mixins";
import auth from "@/mixins/auth";
import EhcUserAvatar from "@/components/ehc-user-avatar.vue";

export default {
  name: "user",
  components: {
    EhcUserAvatar,
  },
  mixins: [mixins, auth],
  data() {
    return {
      userData: {
        uid: null,
        email: null,
        displayName: null,
        phoneNumber: null,
        createdAt: null,
        permissions: [],
        firstName: null,
        lastName: null,
      },
      editingUser: {
        permissions: [],
      },
      userOrganizations: [],
      userProperties: [],
      loading: true,
      editMode: false,
      savingPermissions: false,
      resettingPassword: false,
      confirmDeleteEmail: "",
      emailMessage: "",
      dialog: {
        show: false,
        slotName: "resetPassword",
        title: "Reset Password",
        message: "",
        actions: [
          { label: "Cancel", clickEmit: "cancel", plain: true },
          { type: "spacer" },
          { label: "Send Reset Email", clickEmit: "confirm", color: "primary" },
        ],
      },
      permissionOptions: [
        "superAdmin",
        "admin",
        "member",
        "viewer",
        "realEstateAdmin",
      ],
    };
  },
  computed: {
    userId() {
      return this.$route.params.id;
    },
    userDeactivated() {
      const deletedAt = this.userData.deletedAt;
      return deletedAt !== undefined && deletedAt !== null;
    },
    headerActions() {
      return [
        {
          actionType: "v-btn",
          icon: true,
          iconName: "mdi-arrow-left",
          clickEmit: "back",
          tooltip: "Back to Users",
        },
        {
          actionType: "v-btn",
          label: "Edit Permissions",
          color: "primary",
          dark: true,
          clickEmit: "edit",
        },
      ];
    },
  },
  methods: {
    async loadUser() {
      try {
        this.loading = true;
        const db = firebase.firestore();
        const doc = await db
          .collection("users")
          .doc(this.userId)
          .get({ source: "server" });

        if (doc.exists) {
          const data = doc.data();
          this.userData = {
            uid: doc.id,
            ...data,
          };
          this.editingUser = {
            permissions: [...(data.permissions || [])],
          };
        } else {
          this.$store.commit("setAlert", {
            type: "error",
            text: "User not found",
          });
          this.goBack();
        }
      } catch (error) {
        console.error("Error loading user:", error);
        this.$store.commit("setAlert", {
          type: "error",
          text: "Error loading user: " + error.message,
        });
      } finally {
        this.loading = false;
      }
    },
    async loadUserOrganizations() {
      try {
        const orgs = await this.apiGetOrgsForUid(this.userId);
        this.userOrganizations = orgs.map((org) => ({
          ...org,
          id: org.orgId, // Use orgId from the organization object
        }));
      } catch (error) {
        console.error("Error loading user organizations:", error);
      }
    },
    async loadUserProperties() {
      try {
        const db = firebase.firestore();
        const snapshot = await db
          .collection("properties")
          .where("uid", "==", this.userId)
          .get();

        this.userProperties = [];
        snapshot.forEach((doc) => {
          this.userProperties.push({
            id: doc.id,
            ...doc.data(),
          });
        });
      } catch (error) {
        console.error("Error loading user properties:", error);
      }
    },
    async savePermissions() {
      try {
        this.savingPermissions = true;
        await this.apiUpdateUser(this.userId, {
          permissions: this.editingUser.permissions,
        });
        this.userData.permissions = this.editingUser.permissions;
        this.editMode = false;
        this.$store.commit("setAlert", {
          type: "success",
          text: "Permissions updated successfully",
        });
      } catch (error) {
        console.error("Error saving permissions:", error);
        this.$store.commit("setAlert", {
          type: "error",
          text: "Error saving permissions: " + error.message,
        });
      } finally {
        this.savingPermissions = false;
      }
    },
    async resetPassword() {
      this.dialog = {
        show: true,
        slotName: "resetPassword",
        title: "Reset Password",
        message: "",
        actions: [
          { label: "Cancel", clickEmit: "cancel", plain: true },
          { type: "spacer" },
          { label: "Send Reset Email", clickEmit: "confirm", color: "primary" },
        ],
      };
    },
    async sendPasswordReset() {
      try {
        this.resettingPassword = true;
        await firebase.auth().sendPasswordResetEmail(this.userData.email);
        this.$store.commit("setAlert", {
          type: "success",
          text: "Password reset email sent to " + this.userData.email,
        });
        this.dialog.show = false;
      } catch (error) {
        console.error("Error sending password reset:", error);
        this.$store.commit("setAlert", {
          type: "error",
          text: "Error sending reset email: " + error.message,
        });
      } finally {
        this.resettingPassword = false;
      }
    },
    sendEmail() {
      this.emailMessage = "";
      this.dialog = {
        show: true,
        slotName: "sendEmail",
        title: "Send Email to " + this.userData.email,
        message: "",
        actions: [
          { label: "Cancel", clickEmit: "cancel", plain: true },
          { type: "spacer" },
          { label: "Send Email", clickEmit: "sendConfirm", color: "primary" },
        ],
      };
    },
    async sendEmailAction() {
      if (!this.emailMessage.trim()) {
        this.$store.commit("setAlert", {
          type: "warning",
          text: "Please enter a message",
        });
        return;
      }

      try {
        // Call your email sending function here
        // This would typically call a Cloud Function or API
        this.$store.commit("setAlert", {
          type: "success",
          text: "Email sent to " + this.userData.email,
        });
        this.dialog.show = false;
        this.emailMessage = "";
      } catch (error) {
        console.error("Error sending email:", error);
        this.$store.commit("setAlert", {
          type: "error",
          text: "Error sending email: " + error.message,
        });
      }
    },
    formatDeactivatedAt(deletedAt) {
      if (deletedAt == null) {
        return "—";
      }
      const ms =
        typeof deletedAt === "number"
          ? deletedAt
          : deletedAt.toMillis
            ? deletedAt.toMillis()
            : deletedAt;
      return new Date(ms).toLocaleString();
    },
    confirmDelete() {
      if (this.userDeactivated) {
        return;
      }
      this.confirmDeleteEmail = "";
      this.dialog = {
        show: true,
        slotName: "deleteUser",
        title: "Deactivate User",
        message: "",
        actions: [
          { label: "Cancel", clickEmit: "cancel", plain: true },
          { type: "spacer" },
          {
            label: "Deactivate User",
            clickEmit: "deleteConfirm",
            color: "error",
          },
        ],
      };
    },
    async deleteUser() {
      if (this.confirmDeleteEmail !== this.userData.email) {
        this.$store.commit("setAlert", {
          type: "error",
          text: "Email does not match. Deactivation cancelled.",
        });
        return;
      }

      try {
        const softDeleteUser = firebase
          .functions()
          .httpsCallable("softDeleteUser");
        await softDeleteUser({ uid: this.userId });
        this.markUserDeactivatedInCache(this.userId);
        this.clearUsersListCache();
        this.$store.commit("setAlert", {
          type: "success",
          text: "User deactivated successfully",
        });
        this.dialog.show = false;
        this.goBack();
      } catch (error) {
        console.error("Error deactivating user:", error);
        this.$store.commit("setAlert", {
          type: "error",
          text:
            "Error deactivating user: " +
            (error.message || "Unknown error occurred"),
        });
      }
    },
    goBack() {
      this.$router.back();
    },
    goToOrganization(orgId) {
      this.$router.push({
        name: "organization",
        params: { id: orgId },
      });
    },
    goToProperty(propId) {
      this.$router.push({
        path: `/property/${propId}`,
      });
    },
    formatDate(dateValue) {
      if (!dateValue) return "—";
      const date = new Date(dateValue);
      return date.toLocaleDateString() + " " + date.toLocaleTimeString();
    },
  },
  mounted() {
    this.loadUser();
    this.loadUserOrganizations();
    this.loadUserProperties();
  },
};
</script>

<style scoped>
.gap-2 {
  gap: 8px;
}

.gap-3 {
  gap: 12px;
}

.gap-4 {
  gap: 16px;
}

.text-monospace {
  font-family: "Courier New", monospace;
}
</style>
